import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Phase 36da.99.qspeed — tiny module-level cache for heavy, rarely-changing
 * master lists (patterns, cloths, variants, matchings, …).  Opt-in via the
 * `cacheKey` option.  First call fetches; later calls (e.g. re-opening the
 * create/edit modal) get the cached value INSTANTLY, then a background refresh
 * keeps it fresh (stale-while-revalidate).  Pass the same cacheKey wherever the
 * same data is needed.  Cache is per page-load (cleared on full reload) — safe
 * for masters that don't change mid-session.
 */
const _apiCache = new Map();   // cacheKey -> { data, ts }

/**
 * IN-FLIGHT REQUESTS, keyed the same way.
 *
 * The cache alone is not enough. It is populated when a response ARRIVES, so
 * components that mount together all miss it and all fire. A quotation with 21
 * items mounted 21 ItemBlocks, each asking for /api/patterns — 25 identical
 * requests of 1.8 MB, about 45 MB of the same payload, before the first one
 * returned to populate the cache.
 *
 * This registry holds the PROMISE while a request is running, so the 2nd..Nth
 * caller awaits the first instead of starting another. 25 requests become 1.
 */
const _inFlight = new Map();   // cacheKey -> Promise<payload>
export function clearApiCache(key) {
  if (key == null) _apiCache.clear();
  else _apiCache.delete(key);
  // NOTE: _inFlight is deliberately NOT cleared. A request already on the wire
  // will still resolve, and its waiters are entitled to that answer; the entry
  // removes itself on settle. Clearing it here would let a second request start
  // for data already being fetched — the exact duplication this exists to stop.
}

/**
 * Generic hook for API GET calls.
 * Usage:
 *   const { data, loading, error, refetch } = useApi(vendorApi.getAll);
 *   const { data, loading, error, refetch } = useApi(() => vendorApi.getById(id), [id]);
 *   // cached master list (instant on re-mount, refreshed in background):
 *   const { data } = useApi(patternApi.getAll, [], { cacheKey: "patterns" });
 */
export function useApi(apiFn, deps = [], options = {}) {
  const { cacheKey = null } = options;
  const cached = cacheKey != null ? _apiCache.get(cacheKey) : undefined;
  const [data,    setData]    = useState(cached ? cached.data : null);
  // If we have a cached value we can render immediately (no spinner).
  const [loading, setLoading] = useState(cached ? false : true);
  const [error,   setError]   = useState(null);
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    // Serve cache instantly, then revalidate in the background.
    const hit = cacheKey != null ? _apiCache.get(cacheKey) : undefined;
    if (hit) {
      setData(hit.data);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      let payload;
      const flight = cacheKey != null ? _inFlight.get(cacheKey) : undefined;
      if (flight) {
        // Someone else is already asking for exactly this. Wait for them.
        payload = await flight;
      } else {
        const p = apiFn().then(res => res.data?.data ?? res.data);
        if (cacheKey != null) {
          _inFlight.set(cacheKey, p);
          // Clear on settle, success or failure — a failed request must not
          // wedge the key forever.
          p.finally(() => { _inFlight.delete(cacheKey); });
        }
        payload = await p;
      }
      if (cacheKey != null) _apiCache.set(cacheKey, { data: payload, ts: Date.now() });
      if (mountedRef.current) setData(payload);
    } catch (err) {
      if (mountedRef.current)
        setError(err.response?.data?.message || err.message || "Something went wrong");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    mountedRef.current = true;
    fetch();
    return () => { mountedRef.current = false; };
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

/**
 * Hook for mutations (create / update / delete / toggle).
 *
 * Options:
 *   onSuccess(data)     — called on success
 *   onError(msg)        — called on error (in addition to toast)
 *   successMsg          — if set, shows a success toast
 *   silent              — if true, suppresses the automatic error toast
 */
export function useMutation() {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // Lazy-load toast so pages that don't need it don't break
  // (ToastContext may not be set up in tests/storybook)
  const getToast = () => {
    try {
      // Dynamic import at call time — avoids circular deps
      // eslint-disable-next-line no-undef
      return window.__garmentToast || null;
    } catch { return null; }
  };

  const mutate = useCallback(async (apiFn, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFn();
      const data = res.data?.data ?? res.data;
      options.onSuccess?.(data);

      // Show success toast if caller provided a message
      if (options.successMsg) {
        getToast()?.success(options.successMsg);
      }
      return data;
    } catch (err) {
      // Extract the most useful message
      const backendMsg = err.response?.data?.message;
      const fieldErrors = err.response?.data?.errors; // validation field map
      let msg = backendMsg || err.message || "Something went wrong";

      // For validation errors (400 with field map), list the field messages
      if (fieldErrors && typeof fieldErrors === "object") {
        msg = Object.values(fieldErrors).join(" • ");
      }

      setError(msg);
      options.onError?.(msg);

      // Fire global toast unless caller opts out
      if (!options.silent) {
        getToast()?.error(msg, httpTitle(err.response?.status));
      }
      // Do NOT rethrow — toast already shows the error.
      // Rethrowing causes unhandled promise rejections and CRA dev overlay.
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { mutate, loading, error, clearError: () => setError(null) };
}

function httpTitle(status) {
  if (!status) return "Error";
  if (status === 400) return "Validation Error";
  if (status === 403) return "Permission Denied";
  if (status === 404) return "Not Found";
  if (status === 409) return "Conflict";
  if (status === 500) return "Server Error";
  return `Error ${status}`;
}